import axios from 'axios';
import { fetchDudaSiteAnalyticsReport, listDudaSites, type DudaDateGranularity } from './dudaApi';
import type { DudaAnalyticsCacheV1, DudaSiteAnalyticsSnapshot } from './dudaAnalyticsTypes';

export type DudaDownloadProgress =
  | { phase: 'listing' }
  | { phase: 'downloading'; siteName: string; index: number; total: number };

async function mapPool<T, R>(items: T[], limit: number, worker: (item: T, index: number) => Promise<R>): Promise<R[]> {
  const out: R[] = new Array(items.length);
  let next = 0;
  async function runWorker() {
    while (true) {
      const i = next;
      next += 1;
      if (i >= items.length) {
        return;
      }
      out[i] = await worker(items[i], i);
    }
  }
  const workers = Math.min(Math.max(1, limit), Math.max(1, items.length));
  await Promise.all(Array.from({ length: workers }, () => runWorker()));
  return out;
}

async function safeFetch(fn: () => Promise<unknown>): Promise<{ ok: true; data: unknown } | { ok: false; error: string }> {
  try {
    const data = await fn();
    return { ok: true, data };
  } catch (e) {
    const msg = axios.isAxiosError(e)
      ? String((e.response?.data as { message?: string } | undefined)?.message || e.message)
      : String(e);
    return { ok: false, error: msg };
  }
}

async function fetchOneSiteSnapshot(
  apiUser: string,
  apiPassword: string,
  siteName: string,
  range: { from: string; to: string },
  dateGranularity: DudaDateGranularity
): Promise<DudaSiteAnalyticsSnapshot> {
  const downloadedAt = new Date().toISOString();

  const [tSum, tGeo, tSys, act] = await Promise.all([
    safeFetch(() =>
      fetchDudaSiteAnalyticsReport(apiUser, apiPassword, siteName, {
        ...range,
        result: 'traffic',
        dateGranularity,
      })
    ),
    safeFetch(() =>
      fetchDudaSiteAnalyticsReport(apiUser, apiPassword, siteName, {
        ...range,
        result: 'traffic',
        dateGranularity,
        dimension: 'geo',
      })
    ),
    safeFetch(() =>
      fetchDudaSiteAnalyticsReport(apiUser, apiPassword, siteName, {
        ...range,
        result: 'traffic',
        dateGranularity,
        dimension: 'system',
      })
    ),
    safeFetch(() =>
      fetchDudaSiteAnalyticsReport(apiUser, apiPassword, siteName, {
        ...range,
        result: 'activities',
        dateGranularity,
      })
    ),
  ]);

  return {
    siteName,
    downloadedAt,
    range: { ...range },
    dateGranularity,
    trafficSummary: tSum.ok ? tSum.data : null,
    trafficSummaryError: tSum.ok ? undefined : tSum.error,
    trafficByGeo: tGeo.ok ? tGeo.data : null,
    trafficByGeoError: tGeo.ok ? undefined : tGeo.error,
    trafficBySystem: tSys.ok ? tSys.data : null,
    trafficBySystemError: tSys.ok ? undefined : tSys.error,
    activities: act.ok ? act.data : null,
    activitiesError: act.ok ? undefined : act.error,
  };
}

export type DownloadDudaAnalyticsOptions = {
  apiUser: string;
  apiPassword: string;
  from: string;
  to: string;
  dateGranularity: DudaDateGranularity;
  /** Sites to fetch; default lists all partner sites */
  siteNames?: string[];
  /** Parallel site downloads (each site still runs 4 API calls) */
  siteConcurrency?: number;
  onProgress?: (p: DudaDownloadProgress) => void;
};

/**
 * Downloads every documented analytics variant per site:
 * traffic (totals), traffic+geo, traffic+system, activities — same date range & granularity.
 */
export async function downloadDudaAnalyticsCache(opts: DownloadDudaAnalyticsOptions): Promise<DudaAnalyticsCacheV1> {
  const { apiUser, apiPassword, from, to, dateGranularity, onProgress } = opts;
  const siteConcurrency = opts.siteConcurrency ?? 2;

  onProgress?.({ phase: 'listing' });
  const names =
    opts.siteNames?.length && opts.siteNames.every((n) => n.trim())
      ? opts.siteNames.map((n) => n.trim())
      : (await listDudaSites(apiUser, apiPassword)).map((s) => s.site_name);

  const range = { from, to };
  const downloadedAt = new Date().toISOString();

  const snapshots = await mapPool(names, siteConcurrency, async (siteName, index) => {
    onProgress?.({ phase: 'downloading', siteName, index: index + 1, total: names.length });
    return fetchOneSiteSnapshot(apiUser, apiPassword, siteName, range, dateGranularity);
  });

  const sites: Record<string, DudaSiteAnalyticsSnapshot> = {};
  for (const snap of snapshots) {
    sites[snap.siteName] = snap;
  }

  return {
    version: 1,
    downloadedAt,
    range,
    dateGranularity,
    sites,
  };
}
